// src/components/AdminQuoteImages.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Image, Trash2, RefreshCw, Clock, Copy, Check, ExternalLink, AlertCircle, StepBack } from 'lucide-react';
import ENV from '../config/env';
import LogoutButton from './LogoutButton';

const API_BASE = ENV.API_BASE;
const FILE_SERVER_URL = ENV.FILE_SERVER_URL;

const AdminQuoteImages = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [stats, setStats] = useState({ total: 0, expiredSoon: 0 });
    const [serverStatus, setServerStatus] = useState('checking');

    // Check server status
    const checkServerStatus = async () => {
        try {
            const response = await fetch(`${API_BASE}/health`);
            if (response.ok) {
                setServerStatus('connected');
                setError(null);
            } else {
                setServerStatus('disconnected');
            }
        } catch (error) {
            setServerStatus('disconnected');
            setError('No se puede conectar al servidor de archivos');
            console.error('Server not running:', error);
        }
    };

    // Load images
    const loadImages = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${ENV.FILE_SERVER_URL}/api/quote-images/admin/list`);
            if (!response.ok) throw new Error('Failed to load images');
            const data = await response.json();
            setImages(data);

            const now = new Date();
            const expiredSoon = data.filter(img => {
                const expiresAt = new Date(img.expiresAt);
                const hoursLeft = (expiresAt - now) / (1000 * 60 * 60);
                return hoursLeft < 6;
            }).length;

            setStats({
                total: data.length,
                expiredSoon
            });

        } catch (error) {
            console.error('Error loading quote images:', error);
            setError('Error al cargar las imágenes');
        } finally {
            setLoading(false);
        }
    };

    // Delete image
    const deleteImage = async (filename) => {
        if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) return;

        try {
            const response = await fetch(`${ENV.FILE_SERVER_URL}/api/quote-images/admin/${filename}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to delete');

            setImages(prev => prev.filter(img => img.filename !== filename));
        } catch (error) {
            console.error('Error deleting image:', error);
            alert('Error al eliminar la imagen');
        }
    };

    // Run cleanup
    const runCleanup = async () => {
        try {
            const response = await fetch(`${ENV.FILE_SERVER_URL}/api/quote-images/admin/cleanup`, {
                method: 'POST'
            });

            if (!response.ok) throw new Error('Cleanup failed');

            const result = await response.json();
            alert(result.message);
            loadImages();
        } catch (error) {
            console.error('Error during cleanup:', error);
            alert('Error durante la limpieza');
        }
    };

    // Copy link to clipboard
    const copyLink = (url) => {
        const fullUrl = ENV.FILE_SERVER_URL + url;
        navigator.clipboard.writeText(fullUrl);
        setCopiedId(url);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('es-MX', {
            dateStyle: 'short',
            timeStyle: 'short'
        });
    };

    // Get time left
    const getTimeLeft = (expiresAt) => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const hoursLeft = Math.round((expiry - now) / (1000 * 60 * 60));

        if (hoursLeft < 0) return 'Expirado';
        if (hoursLeft < 1) return '< 1 hora';
        if (hoursLeft === 1) return '1 hora';
        return `${hoursLeft} horas`;
    };

    // Get status color
    const getStatusColor = (expiresAt) => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const hoursLeft = (expiry - now) / (1000 * 60 * 60);

        if (hoursLeft < 0) return 'text-red-500';
        if (hoursLeft < 6) return 'text-yellow-500';
        return 'text-green-500';
    };

    useEffect(() => {
        checkServerStatus();
        loadImages();
    }, []);

    if (loading && images.length === 0) {
        return (
            <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Cargando imágenes...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
                        Imágenes de Cotizaciones
                    </h1>
                    <p className="text-gray-300">
                        Vista previa de los diseños enviados por clientes (expiran en 24h)
                    </p>

                    {/* Server Status */}
                    <div className={`mt-4 inline-block px-4 py-2 rounded-full ${serverStatus === 'connected'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                        }`}>
                        {serverStatus === 'connected'
                            ? '✅ Servidor de archivos conectado'
                            : '❌ Servidor de archivos desconectado'}
                    </div>

                    {error && (
                        <div className="mt-2 text-red-400 text-sm flex items-center justify-center gap-2">
                            <AlertCircle size={16} />
                            ⚠️ {error}
                        </div>
                    )}
                </motion.div>

                {/* Controls */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div></div>
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={loadImages}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                            Actualizar
                        </button>
                        <button
                            onClick={runCleanup}
                            className="bg-accent/80 hover:bg-accent/60 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                        >
                            <Clock size={18} />
                            Borrar cotizaciones
                        </button>
                        <button
                            onClick={() => window.history.back()}
                            className={`flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/30 text-white rounded-lg smooth-transition`}
                            title="Regresar"
                        >
                            <StepBack size={18} />
                            <span>Regresar</span>
                        </button>
                        <LogoutButton />
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                        <p className="text-sm text-gray-400 mb-1">Total Imágenes</p>
                        <p className="text-3xl font-bold text-white">{stats.total}</p>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                        <p className="text-sm text-gray-400 mb-1">Expiran pronto (&lt;6h)</p>
                        <p className="text-3xl font-bold text-yellow-500">{stats.expiredSoon}</p>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                        <p className="text-sm text-gray-400 mb-1">Almacenamiento</p>
                        <p className="text-3xl font-bold text-blue-500">
                            {Math.round(images.reduce((acc, img) => acc + img.size, 0) / 1024)} KB
                        </p>
                    </div>
                </div>

                {/* Images Grid */}
                {error ? (
                    <div className="bg-red-500/20 border border-red-500 rounded-lg p-8 text-center">
                        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
                        <p className="text-red-500">{error}</p>
                    </div>
                ) : images.length === 0 ? (
                    <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
                        <Image size={48} className="mx-auto text-gray-600 mb-4" />
                        <h3 className="text-xl font-bold mb-2">No hay imágenes</h3>
                        <p className="text-gray-400 mb-6">
                            No hay imágenes de cotizaciones disponibles
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {images.map((image) => (
                            <motion.div
                                key={image.filename}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-primary/50 transition-colors"
                            >
                                {/* Image Preview */}
                                <div className="relative aspect-video bg-gray-900">
                                    <img
                                        src={ENV.FILE_SERVER_URL + image.path}
                                        alt="Quote preview"
                                        className="w-full h-full object-contain"
                                        onError={(e) => {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzJkMzc0MiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                                        }}
                                    />
                                </div>

                                {/* Info */}
                                <div className="p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-400">Creado:</span>
                                        <span className="text-sm text-white">{formatDate(image.created)}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-400">Expira:</span>
                                        <span className={`text-sm ${getStatusColor(image.expiresAt)}`}>
                                            {formatDate(image.expiresAt)} ({getTimeLeft(image.expiresAt)})
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-400">Tamaño:</span>
                                        <span className="text-sm text-white">
                                            {Math.round(image.size / 1024)} KB
                                        </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-2 pt-2">
                                        <a
                                            href={ENV.FILE_SERVER_URL + image.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 bg-primary hover:bg-primary/80 text-white px-3 py-2 rounded-lg text-sm text-center transition-colors flex items-center justify-center gap-1"
                                        >
                                            <ExternalLink size={14} />
                                            Ver original
                                        </a>
                                        <button
                                            onClick={() => copyLink(image.path)}
                                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                            title="Copiar link"
                                        >
                                            {copiedId === image.path ? (
                                                <Check size={16} className="text-green-500" />
                                            ) : (
                                                <Copy size={16} />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => deleteImage(image.filename)}
                                            className="px-3 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-500 rounded-lg transition-colors"
                                            title="Eliminar"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminQuoteImages;